import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiCalendar, FiLock } from 'react-icons/fi';
import { useAccountManagement } from '@grcs/hook/useAccountManagement';
import { StaffPasswordAction } from '@grcs/utils/auth/AccountService';
import { staffPasswordError } from '@grcs/utils/auth/staffPasswordPolicy';
import { PasswordField } from '@grcs/views/shared/components/PasswordField';
import { PasswordRequirements } from '@grcs/views/shared/components/PasswordRequirements';

/** Immediate account actions are kept separate from the surrounding profile-save controls. */
export const StaffPasswordManagement = ({
  userToken,
  authUid,
}: {
  userToken?: string;
  authUid?: string;
}) => {
  const accounts = useAccountManagement();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState<StaffPasswordAction | null>(null);
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const subtle = useColorModeValue('gray.50', 'gray.900');
  const unavailable = !authUid || !userToken;
  const disabled = unavailable || !!pending;
  const mismatch = !!confirmation && password !== confirmation;
  const canSetPassword =
    !staffPasswordError(password) && password === confirmation;

  const reset = async (action: StaffPasswordAction) => {
    if (disabled || (action === 'set-password' && !canSetPassword)) return;
    setPending(action);
    try {
      await accounts.resetStaffPassword(userToken!, action, password);
      setPassword('');
      setConfirmation('');
      toast({
        title:
          action === 'reset-birthday'
            ? 'Password reset to birthday'
            : 'Staff password updated',
        description:
          action === 'reset-birthday'
            ? 'The password is now the saved birthday in YYYYMMDD format.'
            : 'The new password is ready to use for staff login.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (error: unknown) {
      toast({
        title: 'Password assistance failed',
        description:
          error instanceof Error
            ? error.message
            : 'Unable to update the password. Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      {unavailable && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          This account needs its authentication link repaired before its
          password can be reset.
        </Alert>
      )}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} alignItems="start">
        <Box
          as="form"
          aria-label="Set staff password"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            reset('set-password');
          }}
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          p={{ base: 4, md: 5 }}
          aria-busy={pending === 'set-password'}
        >
          <VStack align="stretch" spacing={4}>
            <HStack>
              <FiLock aria-hidden="true" />
              <Heading size="sm">Choose a password</Heading>
            </HStack>
            <Text fontSize="sm" color={muted}>
              Set a new password for this account and share it with the account
              owner.
            </Text>
            <PasswordField
              id="staff-new-password"
              label="New staff password"
              value={password}
              onChange={setPassword}
              disabled={disabled}
            />
            <PasswordRequirements password={password} />
            <PasswordField
              id="staff-confirm-password"
              label="Confirm new staff password"
              value={confirmation}
              onChange={setConfirmation}
              disabled={disabled}
              error={mismatch ? 'Passwords do not match.' : undefined}
            />
            <Button
              type="submit"
              colorScheme="blue"
              alignSelf={{ base: 'stretch', sm: 'flex-start' }}
              isDisabled={disabled || !canSetPassword}
              isLoading={pending === 'set-password'}
              loadingText="Updating"
            >
              Set staff password
            </Button>
          </VStack>
        </Box>
        <Box
          borderWidth="1px"
          borderColor={border}
          borderRadius="lg"
          bg={subtle}
          p={{ base: 4, md: 5 }}
        >
          <VStack align="stretch" spacing={4}>
            <HStack>
              <FiCalendar aria-hidden="true" />
              <Heading size="sm">Reset to birthday</Heading>
            </HStack>
            <Text fontSize="sm" color={muted}>
              Use the account’s saved birthday as its password.
            </Text>
            <Box>
              <Text fontSize="sm" fontWeight="semibold">
                YYYYMMDD
              </Text>
              <Text fontSize="xs" color={muted} mt={1}>
                For example, May 21, 1980 becomes 19800521.
              </Text>
            </Box>
            <Text fontSize="sm" color={muted}>
              Save any changes to the date of birth before resetting.
            </Text>
            <Button
              type="button"
              variant="outline"
              alignSelf={{ base: 'stretch', sm: 'flex-start' }}
              isDisabled={disabled}
              isLoading={pending === 'reset-birthday'}
              loadingText="Resetting"
              onClick={() => reset('reset-birthday')}
            >
              Reset password to birthday
            </Button>
          </VStack>
        </Box>
      </SimpleGrid>
    </VStack>
  );
};
